import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import Logo from "@/components/Logo";
import { useI18n, SUPPORTED_LANGS } from "@/i18n/I18nProvider";

function useCurrentLang() {
  const { pathname } = useLocation();
  const seg = pathname.split("/")[1];
  return SUPPORTED_LANGS.includes(seg) ? seg : "ar";
}

export default function Header() {
  const { t } = useI18n();
  const lang = useCurrentLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const switchLang = (nextLang) => {
    if (nextLang === lang) return;
    const parts = location.pathname.split("/");
    if (SUPPORTED_LANGS.includes(parts[1])) {
      parts[1] = nextLang;
    } else {
      parts.splice(1, 0, nextLang);
    }
    const next = parts.join("/") || `/${nextLang}`;
    navigate(next + location.search + location.hash, { replace: false });
  };

  const base = `/${lang}`;

  const scrollToAnchor = (id) => (e) => {
    if (location.pathname === base || location.pathname === `${base}/`) {
      e.preventDefault();
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const links = [
    { id: "why", label: t("nav.whyChoose") },
    { id: "how", label: t("nav.howItWorks") },
    { id: "transparency", label: t("nav.transparency") },
    { id: "contact", label: t("nav.contact") },
  ];

  return (
    <header className="dma-header">
      <div className="dma-header__inner">
        <Link to={base} className="dma-header__brand" aria-label="Dataminerarg">
          <Logo />
        </Link>

        <nav className="dma-header__nav" aria-label="primary">
          <NavLink to={base} end className="dma-header__link">
            {t("nav.home")}
          </NavLink>
          {links.map((l) => (
            <a
              key={l.id}
              href={`${base}#${l.id}`}
              onClick={scrollToAnchor(l.id)}
              className="dma-header__link"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="dma-header__right">
          <div className="dma-lang" role="group" aria-label={t("nav.language")}>
            <Globe size={16} className="dma-lang__icon" aria-hidden="true" />
            <button
              type="button"
              className={`dma-lang__btn ${lang === "ar" ? "is-active" : ""}`}
              onClick={() => switchLang("ar")}
            >
              ES
            </button>
            <span className="dma-lang__sep">/</span>
            <button
              type="button"
              className={`dma-lang__btn ${lang === "en" ? "is-active" : ""}`}
              onClick={() => switchLang("en")}
            >
              EN
            </button>
          </div>
          <a href={`${base}#contact`} onClick={scrollToAnchor("contact")} className="dma-btn dma-btn--primary dma-header__cta">
            {t("hero.ctaPrimary")}
          </a>
          <button
            type="button"
            className="dma-header__toggle"
            aria-expanded={open}
            aria-label={open ? t("nav.closeMenu") : t("nav.openMenu")}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="dma-header__mobile" role="dialog" aria-modal="true">
          <NavLink to={base} end className="dma-header__mobile-link">
            {t("nav.home")}
          </NavLink>
          {links.map((l) => (
            <a
              key={l.id}
              href={`${base}#${l.id}`}
              onClick={(e) => {
                setOpen(false);
                scrollToAnchor(l.id)(e);
              }}
              className="dma-header__mobile-link"
            >
              {l.label}
            </a>
          ))}
          <div className="dma-header__mobile-lang">
            <button type="button" className={`dma-lang__btn ${lang === "ar" ? "is-active" : ""}`} onClick={() => switchLang("ar")}>
              Español
            </button>
            <button type="button" className={`dma-lang__btn ${lang === "en" ? "is-active" : ""}`} onClick={() => switchLang("en")}>
              English
            </button>
          </div>
          <a
            href={`${base}#contact`}
            onClick={(e) => {
              setOpen(false);
              scrollToAnchor("contact")(e);
            }}
            className="dma-btn dma-btn--primary"
          >
            {t("hero.ctaPrimary")}
          </a>
        </div>
      )}
    </header>
  );
}
